<template>
  <Teleport to="body">
    <div v-if="showLoginModal" class="login-modal-mask" @click.self="onMaskClick">
      <div class="login-card" @click.stop>
        <button type="button" class="login-close" aria-label="关闭" @click="close">×</button>
        <div class="login-left">
          <div class="login-header">
            <h1 class="login-title">登录/注册</h1>
          </div>

          <div class="login-form">
            <div class="field">
              <div class="phone-input-row">
                <div class="country-code">+86</div>
                <input
                  v-model="phone"
                  type="tel"
                  maxlength="11"
                  placeholder="请输入手机号"
                />
              </div>
            </div>

            <div class="field">
              <div class="code-input-row">
                <input
                  v-model="code"
                  type="text"
                  maxlength="6"
                  placeholder="请输入验证码"
                />
                <button
                  class="code-btn"
                  :disabled="countdown > 0"
                  type="button"
                  @click="sendCode"
                >
                  {{ countdown > 0 ? countdown + 's' : '获取验证码' }}
                </button>
              </div>
            </div>

            <button
              class="primary-btn"
              type="button"
              :disabled="loading"
              @click="handleLogin"
            >
              {{ loading ? '登录中...' : '登录 / 注册' }}
            </button>
          </div>

          <p class="agreement">
            登录或注册即代表同意
            <a href="#" @click.prevent="goUserPolicy">用户协议</a>
            和
            <a href="#" @click.prevent="goUserPolicy">隐私政策</a>
          </p>
        </div>

        <div class="login-right">
          <img
            class="login-illustration"
            src="@/assets/images/M-09登录大图.png"
            alt="登录插画"
          />
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { ref, computed, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useUserStore } from '@/stores/user'
import { useLoginModalStore } from '@/stores/loginModal'
import { showToast } from '@/utils/toast'
import { getApiErrorMessage } from '@/utils/apiError'
import { isMobileBrowser } from '@/utils/mobileLayout'

export default {
  name: 'LoginModal',
  setup() {
    const router = useRouter()
    const userStore = useUserStore()
    const loginModalStore = useLoginModalStore()
    const { visible, force } = storeToRefs(loginModalStore)

    const phone = ref('')
    const code = ref('')
    const loading = ref(false)
    const countdown = ref(0)
    const showLoginModal = computed(() => visible.value && !isMobileBrowser())
    let timer = null

    function close() {
      loginModalStore.close()
    }
    function onMaskClick() {
      close()
    }

    function goUserPolicy() {
      loginModalStore.close()
      router.push('/userpolicy')
    }

    async function sendCode() {
      if (!/^1[3-9]\d{9}$/.test(phone.value)) {
        showToast('请输入正确的手机号')
        return
      }
      try {
        const res = await userStore.sendCode(phone.value)
        if (res?.code) {
          code.value = res.code
          showToast('验证码: ' + res.code)
        } else {
          showToast('验证码已发送')
        }
        countdown.value = 60
        timer = setInterval(() => {
          countdown.value--
          if (countdown.value <= 0 && timer) {
            clearInterval(timer)
            timer = null
          }
        }, 1000)
      } catch (e) {
        showToast(getApiErrorMessage(e, '发送失败'))
      }
    }

    async function handleLogin() {
      if (!/^1[3-9]\d{9}$/.test(phone.value)) {
        showToast('请输入正确的手机号')
        return
      }
      if (!/^\d{4,6}$/.test(code.value)) {
        showToast('请输入验证码')
        return
      }
      loading.value = true
      try {
        await userStore.login(phone.value, code.value)
        showToast('登录成功')
        // 强制登录时：允许关闭并恢复交互
        loginModalStore.closeForce()
      } catch (e) {
        showToast(getApiErrorMessage(e, '登录失败'))
      } finally {
        loading.value = false
      }
    }

    onUnmounted(() => {
      if (timer) clearInterval(timer)
    })

    return {
      visible,
      force,
      showLoginModal,
      close,
      onMaskClick,
      goUserPolicy,
      phone,
      code,
      loading,
      countdown,
      sendCode,
      handleLogin
    }
  }
}
</script>

<style scoped>
.login-modal-mask {
  position: fixed;
  inset: 0;
  z-index: 10020;
  padding: 40px 24px;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
}

.login-card {
  position: relative;
  z-index: 1;
  width: 100%;
  height: 442px;
  max-width: 777px;
  background: #1a1a1a;
  border-radius: 24px;
  display: flex;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.03);
  cursor: default;
}

.login-close {
  position: absolute;
  right: 14px;
  top: 12px;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.9);
  font-size: 22px;
  line-height: 36px;
  cursor: pointer;
}
.login-close:hover {
  background: rgba(255, 255, 255, 0.1);
}

.login-left {
  flex: 1;
  padding: 50px 40px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.login-header {
  margin-bottom: 32px;
  width: 296px;
  text-align: center;
}

.login-title {
  margin: 0 0 12px;
  font-weight: 500;
  font-size: 18px;
  color: #ffffff;
  text-align: center;
}

.login-form {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.field {
  margin-bottom: 12px;
}

.phone-input-row,
.code-input-row {
  width: 296px;
  height: 40px;
  background: #2a2a2a;
  border-radius: 20px;
  border: 1px solid #3a3a3a;
  display: flex;
  align-items: center;
  padding: 0 16px;
}

.phone-input-row input,
.code-input-row input {
  flex: 1;
  height: 100%;
  border: none;
  outline: none;
  background: transparent;
  color: #ffffff;
  font-weight: 400;
  font-size: 14px;
}

.phone-input-row input::placeholder,
.code-input-row input::placeholder {
  color: #767676;
  font-weight: 400;
  font-size: 14px;
}

.country-code {
  margin-right: 12px;
  padding-right: 12px;
  font-size: 14px;
  color: #ffffff;
  border-right: 1px solid rgba(255, 255, 255, 0.12);
  white-space: nowrap;
}

.code-input-row {
  padding-right: 6px;
}

.code-btn {
  margin-left: 8px;
  padding: 6px 14px;
  border-radius: 999px;
  border: none;
  font-size: 12px;
  cursor: pointer;
  background: #2a2a2a;
  color: #7657ff;
  white-space: nowrap;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.code-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.code-btn:not(:disabled):hover {
  opacity: 0.9;
  transform: translateY(-1px);
}

.primary-btn {
  width: 296px;
  height: 36px;
  margin-top: 40px;
  border-radius: 999px;
  border: none;
  background: #7657ff;
  color: #ffffff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease;
}

.primary-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  box-shadow: none;
}

.primary-btn:not(:disabled):hover {
  transform: translateY(-1px);
}

.agreement {
  margin-top: 20px;
  font-size: 12px;
  font-weight: 500;
  color: #ffffff;
  text-align: center;
  width: 296px;
}

.agreement a {
  color: #7657ff;
  text-decoration: none;
  align-content: center;
}

.agreement a:hover {
  text-decoration: underline;
}

.login-right {
  width: 420px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.login-illustration {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

@media (max-width: 768px) {
  .login-modal-mask {
    padding: 24px 16px;
  }

  .login-card {
    flex-direction: column;
  }

  .login-right {
    width: 100%;
    order: -1;
    padding: 16px 16px 0;
  }

  .login-left {
    padding: 24px 20px 20px;
  }
}
</style>
